"use client";

import { ExternalLink } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import { useOpenExternal } from "@/shared/lib/hooks/use-open-external";

type OpggButtonProps = {
  url: string;
  title?: string;
  className?: string;
  /** Текст на кнопке (по умолчанию «OP.GG»). Напр. «Мультисёрч» для всей команды. */
  label?: string;
  /**
   * Куда прижать всплывающую подсказку. По умолчанию она центрируется по кнопке
   * и потому шире кнопки выступает в обе стороны. В прижатой к правому краю
   * ячейке (таблицы состава) этот выступ попадал в область прокрутки контейнера
   * с `overflow-x` — и полоса прокрутки висела даже на широком экране.
   * `end` убирает выступ вправо, не меняя вид подсказки.
   */
  tooltipAlign?: "center" | "end";
};

export function OpggButton({
  url,
  title,
  className,
  label = "OP.GG",
  tooltipAlign = "center",
}: OpggButtonProps) {
  const t = useTranslations("Common.opgg");
  // В Tauri webview2 `<a target="_blank">` молча блокируется. Без этого хука
  // юзеры в десктопном клиенте видели «ничего не происходит» по клику OP.GG.
  // На сайте openExternal — no-op, native target=_blank работает сам.
  const openExternal = useOpenExternal();
  return (
    <span className="relative inline-flex group/opgg">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={title ?? t("openInOpgg")}
        className={
          className ??
          "inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-semibold rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/15"
        }
        onClick={openExternal(url)}
        onPointerDownCapture={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onTouchStartCapture={(e) => e.stopPropagation()}
        onClickCapture={(e) => e.stopPropagation()}
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {label}
      </a>
      <span
        className={cn(
          "pointer-events-none absolute bottom-full mb-2 whitespace-nowrap rounded-md border bg-popover text-popover-foreground px-2 py-1 text-xs shadow-md opacity-0 translate-y-1 transition-all duration-150 group-hover/opgg:opacity-100 group-hover/opgg:translate-y-0 group-focus-within/opgg:opacity-100 group-focus-within/opgg:translate-y-0",
          tooltipAlign === "end" ? "right-0" : "left-1/2 -translate-x-1/2",
        )}
      >
        {title ?? t("openInOpgg")}
      </span>
    </span>
  );
}
